import { rollbar } from 'src/helpers';
import toast from 'src/components/Toast';
import { Yup } from './validation';

export const handleRequest = async (promise, showNotification = false) => {
  const response = await promise;
  //TODO: get rid of next line after API refactoring
  if (showNotification && response?.messages) {
    toast.info(response.messages.join(''));
  }
  return response;
};

type args = {
  requests: any;
  actions: { setStatus: () => void; setSubmitting: () => void };
  successMsg?: string;
  shouldPropagateError?: boolean;
};

export const formikSubmitWrapper = requestsHandler => async ({ requests, actions, successMsg = '' }: args) => {
  actions.setStatus();
  try {
    const response = await requestsHandler(requests);

    if (successMsg) {
      toast.info(successMsg);
    }

    return response;
  } catch (error) {
    if (error?.message?.includes('[mobx-state-tree]')) {
      // formik suppress error propagation in submit handler so we need to catch it here
      rollbar.error(error);
      return;
    }
    if (error.messages) {
      //show global form errors
      actions.setStatus({ errors: error.messages });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return Promise.reject(error);
  } finally {
    actions.setSubmitting(false);
  }
};

export const handleFormikParallelSubmit = formikSubmitWrapper(async (promise: Promise<any> | Promise<any>[]) => {
  let response = null;
  if (Array.isArray(promise)) {
    const requests = promise.map(request => handleRequest(request));
    response = await Promise.all(requests);
  } else {
    response = await handleRequest(promise);
  }
  return response;
});

export const handleFormikSequentialSubmit = formikSubmitWrapper(
  async (requests: (() => Promise<any>)[] | (() => Promise<any>)[][]) => {
    let response = null;
    for (const request of requests) {
      if (Array.isArray(request)) {
        await Promise.all(request.map(r => handleRequest(r())));
      } else {
        response = await handleRequest(request(response));
      }
    }
    return response;
  }
);

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const fileTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif'
];
// validations for custom anwsers
export const validateFile = () =>
  Yup.array().of(
    Yup.mixed()
      .test('fileSize', 'File is too large', value => {
        if (!value || typeof value.values !== 'function') return true;
        for (const file of value.values()) {
          if (file.size) {
            return file.size < MAX_FILE_SIZE;
          }
        }
        return false;
      })
      .test('fileType', 'Incorrect file type', value => {
        if (!value || typeof value.values !== 'function') return true;

        for (const file of value.values()) {
          if (file.type) {
            return fileTypes.includes(file.type);
          }
        }
        return false;
      })
  );

export const validateFormikForm = async ({ validateForm, setErrors, setTouched }) => {
  const errors = await validateForm();
  const isValid = Object.keys(errors).length === 0;
  if (!isValid) {
    setErrors(errors);
    setTouched(errors);
    return Promise.reject();
  }
};

export const validateYupSchema = async ({ schema, values, setSubmitting, setErrors, setTouched }) => {
  setSubmitting(true);
  schema.validateSync(values).catch(errors => {
    setErrors(errors);
    setTouched(errors);
    setSubmitting(false);
    return Promise.reject();
  });
};

// github.com/jaredpalmer/formik/issues/1580
// it doesn't return handler promise anyway
export function fixedFormikSubmit({ submitForm, validateForm }) {
  return new Promise((resolve, reject) => {
    submitForm()
      .then(validateForm)
      .then(errors => {
        const isValid = Object.keys(errors).length === 0;
        if (isValid) {
          resolve();
        } else {
          reject();
        }
      })
      .catch(e => {
        reject(e);
      });
  });
}
